import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Archive, Check, Info, Loader2, Search, Send, UserMinus, X } from 'lucide-react';
import { Profile } from '../../types';
import { propsFundoModal } from '../../src/lib/backdropModal';
import {
  acaoNaConta, descreverVinculos, podeReceberPendencias, rotuloConfirmar, somaVinculos,
  totalTransferivel, vinculosQueSobram,
  type AcaoNaConta, type AcaoSaida, type DestinoPendencias, type Pendencias, type VinculosExclusao,
} from '../../src/lib/pendenciasUsuario';
import {
  contarPendencias, contarVinculosExclusao, transferirPendencias, type ResultadoTransferencia,
} from '../../src/lib/pendenciasUsuarioDb';

const ROLE_LABEL: Record<string, string> = {
  adm: 'Administrador',
  gerente: 'Gerente',
  vendedor: 'Vendedor',
  analista: 'Analista',
  parceiro: 'Parceiro',
  gerente_parcerias: 'Gerente de Parcerias',
  vendedor_parceiro: 'Vendedor Parceiro',
  motoboy: 'Motoboy',
};

const fmt = (n: number) => n.toLocaleString('pt-BR');

export interface ResultadoSaida {
  conta: AcaoNaConta;
  transferido: ResultadoTransferencia | null;
  para: Profile | null;
}

interface PendenciasUsuarioModalProps {
  /** Quem está saindo. */
  usuario: Profile;
  acao: AcaoSaida;
  /** Quem está operando a tela. */
  ator: Profile;
  /** Perfis da tela de usuários — a janela recorta quem pode receber. */
  perfis: Profile[];
  onClose: () => void;
  /** Inativa ou exclui a conta pelo manage-users. Deve lançar erro com a causa. */
  executarNaConta: (acao: 'inativar' | 'excluir') => Promise<void>;
  onConcluido: (resultado: ResultadoSaida) => void;
}

/**
 * Pergunta o que fazer com o que está aberto no nome de quem sai da empresa:
 * transferir para alguém ativo ou só arquivar. Regras em src/lib/pendenciasUsuario.ts.
 */
const PendenciasUsuarioModal: React.FC<PendenciasUsuarioModalProps> = ({
  usuario, acao, ator, perfis, onClose, executarNaConta, onConcluido,
}) => {
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [pendencias, setPendencias] = useState<Pendencias | null>(null);
  const [vinculos, setVinculos] = useState<VinculosExclusao | null>(null);

  const [destino, setDestino] = useState<DestinoPendencias | null>(acao === 'transferir' ? 'transferir' : null);
  const [paraId, setParaId] = useState('');
  const [busca, setBusca] = useState('');

  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const nome = usuario.full_name || 'Usuário';
  const primeiroNome = nome.split(' ')[0];
  const jaInativo = usuario.status === 'inactive';

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const [p, v] = await Promise.all([
          contarPendencias(usuario.id),
          acao === 'excluir' ? contarVinculosExclusao(usuario.id) : Promise.resolve(null),
        ]);
        if (!ativo) return;
        setPendencias(p);
        setVinculos(v);
      } catch (e: any) {
        if (ativo) setErroCarga(e?.message || 'Não foi possível contar as pendências.');
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => { ativo = false; };
  }, [usuario.id, acao]);

  const candidatos = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return perfis
      .filter(p => podeReceberPendencias(ator, p, usuario.id))
      .filter(p => !termo || (p.full_name || '').toLowerCase().includes(termo))
      .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
  }, [perfis, ator, usuario.id, busca]);

  const temPendencias = !!pendencias && totalTransferivel(pendencias) > 0;
  // Sem pendência não há o que escolher; para as contas, "arquivar" = nada muda de dono.
  const destinoEfetivo: DestinoPendencias = temPendencias ? (destino ?? 'arquivar') : 'arquivar';

  const restantes = vinculos && pendencias ? vinculosQueSobram(vinculos, pendencias, destinoEfetivo) : null;
  const conta = acaoNaConta(acao, restantes ? somaVinculos(restantes) : 0, jaInativo);
  const exclusaoViraInativacao = acao === 'excluir' && conta !== 'excluir';

  const para = perfis.find(p => p.id === paraId) || null;
  const faltaEscolha = temPendencias && (destino === null || (destino === 'transferir' && !para));
  const nadaAFazer = conta === 'nenhuma' && !(temPendencias && destino === 'transferir');
  const podeConfirmar = !carregando && !erroCarga && !salvando && !faltaEscolha && !nadaAFazer;

  const confirmar = async () => {
    if (!podeConfirmar || !pendencias) return;
    setSalvando(true);
    setErro(null);
    let transferido: ResultadoTransferencia | null = null;
    try {
      if (temPendencias && destino === 'transferir' && para) {
        transferido = await transferirPendencias({
          de: { id: usuario.id, nome },
          para: { id: para.id, nome: para.full_name || 'novo responsável' },
          atorId: ator.id,
        });
      }
      if (conta !== 'nenhuma') {
        try {
          await executarNaConta(conta);
        } catch (e: any) {
          const jaFoi = transferido
            ? `As pendências já passaram para ${para?.full_name} (${fmt(transferido.tarefas)} tarefa(s), ${fmt(transferido.negociacoes)} negociação(ões)), mas `
            : '';
          throw new Error(`${jaFoi}${conta === 'excluir' ? 'a exclusão' : 'a inativação'} falhou: ${e?.message || 'erro desconhecido'}`);
        }
      }
      onConcluido({ conta, transferido, para });
    } catch (e: any) {
      setErro(e?.message || 'Erro ao concluir.');
      setSalvando(false);
    }
  };

  const titulo = acao === 'inativar' ? `Inativar ${nome}` : acao === 'excluir' ? `Excluir ${nome}` : `Pendências de ${nome}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center sm:p-4 bg-black/50"
      {...propsFundoModal(() => { if (!salvando) onClose(); })}
    >
      <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg flex flex-col overflow-hidden max-h-[92vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`p-2 rounded-xl shrink-0 ${acao === 'excluir' ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'}`}>
              <UserMinus size={18} />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white truncate">{titulo}</h2>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                {acao === 'transferir' ? 'Passe o que ficou aberto para alguém ativo.' : 'O que fazer com o que está aberto no nome desta pessoa?'}
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={salvando} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0 disabled:opacity-40">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {carregando && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 size={16} className="animate-spin" /> Contando pendências...
            </div>
          )}

          {erroCarga && (
            <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
              {erroCarga}
            </div>
          )}

          {!carregando && pendencias && (
            <>
              {/* Resumo do que está aberto */}
              <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 p-4 space-y-2">
                {temPendencias ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{fmt(pendencias.tarefas)}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{pendencias.tarefas === 1 ? 'tarefa ou visita pendente' : 'tarefas e visitas pendentes'}</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{fmt(pendencias.negociacoes)}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{pendencias.negociacoes === 1 ? 'negociação em aberto' : 'negociações em aberto'}</p>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 pt-1">
                      Em aberto = em andamento, simulação realizada ou termo enviado. Vendas assinadas, aprovadas, perdidas e reprovadas continuam com {primeiroNome}.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-300">Nada aberto no nome de {primeiroNome}.</p>
                )}
                {pendencias.solicitacoes > 0 && (
                  <p className="flex gap-1.5 text-[11px] text-amber-700 dark:text-amber-400 pt-1">
                    <Info size={13} className="shrink-0 mt-px" />
                    {fmt(pendencias.solicitacoes)} {pendencias.solicitacoes === 1 ? 'solicitação aberta' : 'solicitações abertas'} na Central de Solicitações não {pendencias.solicitacoes === 1 ? 'entra' : 'entram'} aqui — redirecione por lá.
                  </p>
                )}
              </div>

              {/* Escolha: transferir ou arquivar */}
              {temPendencias && (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setDestino('transferir')}
                    className={`w-full text-left flex gap-3 p-3 rounded-xl border transition-colors ${destino === 'transferir' ? 'border-primary/50 bg-primary/5' : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                  >
                    <Send size={18} className={`shrink-0 mt-0.5 ${destino === 'transferir' ? 'text-primary' : 'text-gray-400'}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">Transferir para outra pessoa</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Tarefas e negociações em aberto passam para alguém ativo. Ela recebe um aviso no sino por item, e o SLA de cada lead começa a contar para ela.
                      </p>
                    </div>
                  </button>

                  {acao !== 'transferir' && (
                    <button
                      type="button"
                      onClick={() => setDestino('arquivar')}
                      className={`w-full text-left flex gap-3 p-3 rounded-xl border transition-colors ${destino === 'arquivar' ? 'border-primary/50 bg-primary/5' : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                    >
                      <Archive size={18} className={`shrink-0 mt-0.5 ${destino === 'arquivar' ? 'text-primary' : 'text-gray-400'}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Só arquivar</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Nada muda de dono. As tarefas saem das listas, do Kanban e dos painéis; as negociações continuam no CRM no nome de {primeiroNome}. Dá para transferir depois pelo menu do usuário.
                        </p>
                      </div>
                    </button>
                  )}
                </div>
              )}

              {/* Para quem */}
              {temPendencias && destino === 'transferir' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      value={busca}
                      onChange={e => setBusca(e.target.value)}
                      placeholder="Para quem? Buscar pessoa ativa..."
                      className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                  <div className="max-h-56 overflow-y-auto space-y-1 -mx-1 px-1">
                    {candidatos.map(p => {
                      const sel = p.id === paraId;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setParaId(p.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors border ${sel ? 'bg-primary/5 border-primary/40' : 'bg-gray-50/60 dark:bg-gray-700/30 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700/60'}`}
                        >
                          <div className="w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">
                            {(p.full_name || '—').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{p.full_name}</p>
                            <p className="text-[11px] text-gray-400 truncate">
                              {ROLE_LABEL[p.role] || p.role}{p.departamento ? ` · ${p.departamento}` : ''}
                            </p>
                          </div>
                          {sel && <Check size={16} className="text-primary shrink-0" />}
                        </button>
                      );
                    })}
                    {candidatos.length === 0 && <p className="text-center text-sm text-gray-400 py-4">Ninguém encontrado.</p>}
                  </div>
                </div>
              )}

              {/* Exclusão que não pode acontecer */}
              {exclusaoViraInativacao && restantes && (
                <div className="flex gap-2 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-300">
                  <AlertTriangle size={15} className="shrink-0 mt-px" />
                  <p>
                    Não dá para excluir: {primeiroNome} tem histórico no sistema ({descreverVinculos(restantes).join(', ')}), e apagar a conta levaria esse histórico junto.{' '}
                    {jaInativo
                      ? 'A conta já está inativa — sem acesso e fora das listas.'
                      : 'A conta será inativada: perde o acesso e sai das listas, e o histórico fica.'}
                  </p>
                </div>
              )}

              {acao === 'inativar' && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Ao inativar, {primeiroNome} perde o acesso ao sistema. Dá para reativar depois.
                </p>
              )}

              {erro && (
                <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
                  {erro}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-700 flex gap-2">
          <button
            onClick={onClose}
            disabled={salvando}
            className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40"
          >
            {nadaAFazer && !carregando ? 'Fechar' : 'Cancelar'}
          </button>
          {!(nadaAFazer && !carregando) && (
            <button
              onClick={confirmar}
              disabled={!podeConfirmar}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl text-white transition-opacity disabled:opacity-40 ${conta === 'excluir' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:opacity-90'}`}
            >
              {salvando && <Loader2 size={15} className="animate-spin" />}
              {salvando
                ? 'Processando...'
                : temPendencias && destino === null
                  ? 'Escolha uma opção'
                  : rotuloConfirmar(destinoEfetivo, conta, temPendencias)}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PendenciasUsuarioModal;
